import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import {
  getReport,
  reviewReport,
  revertReportToDraft,
  getReportComments,
  createComment,
  deleteComment,
} from '../lib/api';
import { getUserFullDisplay } from '../lib/user';
import CommentSection from '../components/CommentSection';
import RichTextDisplay from '../components/RichTextDisplay';
import { Card, Button, Tag, Space, Typography, Spin, Alert, Descriptions, Divider } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, RollbackOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export default function ReportDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [report, setReport] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    loadReport();
    loadComments();
  }, [id]);

  const loadReport = async () => {
    try {
      const data = await getReport(Number(id));
      setReport(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    if (!id) return;
    setCommentsLoading(true);
    try {
      const data = await getReportComments(Number(id));
      setComments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async (content: string) => {
    if (!id) return;
    await createComment({ reportId: Number(id), content });
    await loadComments();
  };

  const handleDeleteComment = async (commentId: number) => {
    await deleteComment(commentId);
    await loadComments();
  };

  const handleReview = async () => {
    setReviewing(true);
    try {
      await reviewReport(Number(id));
      setReport({ ...report, status: 'reviewed' });
    } catch (err) {
      console.error(err);
    } finally {
      setReviewing(false);
    }
  };

  const handleRevertToDraft = async () => {
    if (!window.confirm('確定要將此週報退回為草稿嗎？員工將可以重新編輯。')) return;

    setReverting(true);
    try {
      await revertReportToDraft(Number(id));
      setReport({ ...report, status: 'draft' });
    } catch (err) {
      console.error(err);
    } finally {
      setReverting(false);
    }
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      draft: { color: 'default', label: '草稿' },
      submitted: { color: 'processing', label: '已提交' },
      reviewed: { color: 'success', label: '已審核' },
    };
    const { color, label } = config[status] || config.draft;
    return <Tag color={color}>{label}</Tag>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <Card>
        <Alert
          title="週報不存在"
          description={<Link to="/reports">返回週報列表</Link>}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const canReview = user?.role !== 'employee' &&
    report.status === 'submitted' &&
    report.userId !== user?.id;

  const canRevert = user?.role !== 'employee' &&
    report.userId !== user?.id &&
    (report.status === 'submitted' || report.status === 'reviewed');

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Link to="/reports">
        <Button icon={<ArrowLeftOutlined />}>返回週報列表</Button>
      </Link>

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              <FileTextOutlined /> 週報詳情
            </Title>
            {getStatusTag(report.status)}
          </Space>
        }
        extra={
          <Space>
            {canReview && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleReview}
                loading={reviewing}
              >
                標記為已審核
              </Button>
            )}
            {canRevert && (
              <Button
                danger
                icon={<RollbackOutlined />}
                onClick={handleRevertToDraft}
                loading={reverting}
              >
                退回草稿
              </Button>
            )}
          </Space>
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label="週次">
            {new Date(report.weekStart).toLocaleDateString('zh-TW')}
          </Descriptions.Item>
          {report.user && (
            <Descriptions.Item label="提交人">
              {getUserFullDisplay(report.user)}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider />

        <div>
          <Title level={5}>本週完成事項</Title>
          {report.reportTasks && report.reportTasks.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>關聯任務:</Text>
              <Space orientation="vertical" style={{ marginTop: 8, width: '100%' }}>
                {report.reportTasks.map((rt: any) => (
                  <Link key={rt.task.id} to={`/tasks/${rt.task.id}`}>
                    <LinkOutlined /> {rt.task.title}
                  </Link>
                ))}
              </Space>
            </div>
          )}
          <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <RichTextDisplay content={report.completedTasks} emptyText="未填寫" />
          </div>
        </div>

        <Divider />

        <div>
          <Title level={5}>下週計畫</Title>
          <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <RichTextDisplay content={report.nextWeekPlan} emptyText="未填寫" />
          </div>
        </div>

        <Divider />

        <div>
          <Title level={5}>改善建議</Title>
          <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <RichTextDisplay content={report.improvements} emptyText="未填寫" />
          </div>
        </div>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary">
            建立時間: {new Date(report.createdAt).toLocaleString('zh-TW')} |
            最後更新: {new Date(report.updatedAt).toLocaleString('zh-TW')}
          </Text>
        </div>
      </Card>

      <Card title={<>討論區</>}>
        <CommentSection
          comments={comments}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          loading={commentsLoading}
        />
      </Card>
    </Space>
  );
}
